import { useClient } from '@cortex/react-query';
import type { CortexClient } from '@cortex/sdk';
import type { CreatePersonalAccessTokenResult, PersonalAccessToken } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type CSSProperties, type ReactNode } from 'react';

import { PageHeader } from '../components/common/PageHeader';
import { mcpServerBaseUrl } from '../lib/mcp-server';

export const Route = createFileRoute('/api-keys')({ component: ApiKeysPage });

const stack6: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-6)' };
const stack4: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' };
const stack2: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' };
const rowH: CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' };
const rowHWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3)',
  flexWrap: 'wrap',
};

function patListKey() {
  return ['personal-access-tokens'] as const;
}

/** Best display name for the PAT's creator: name → email → opaque user id. */
function createdByLabel(row: PersonalAccessToken): string {
  return row.createdByName || row.createdByEmail || row.keycloakUserId || 'unknown';
}

/** Dates arrive as ISO strings over HTTP even though the type says `Date`. */
function fmtDate(v: Date | string | null | undefined): string {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ApiKeysPage() {
  const client = useClient();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: patListKey(),
    queryFn: () => client.http.get('v1/auth/tokens').json<PersonalAccessToken[]>(),
  });
  const refetch = () => qc.invalidateQueries({ queryKey: patListKey() });

  return (
    <div style={stack6}>
      <PageHeader title="API keys" />

      <ConnectInstructions />

      <div className="card">
        <div className="card-header">
          <span className="t-mono-label">API KEYS · NEW</span>
          <h3 className="t-h3">Create a token</h3>
        </div>
        <div className="card-body">
          <CreatePatForm client={client} onCreated={refetch} />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="t-mono-label">API KEYS</span>
          <h3 className="t-h3">Your personal access tokens</h3>
        </div>
        <div className="card-body">
          {list.isLoading ? (
            <p className="t-sm t-secondary">Loading…</p>
          ) : list.error ? (
            <p className="t-sm" style={{ color: 'var(--status-danger)' }}>
              Failed to load: {(list.error as Error).message}
            </p>
          ) : !list.data || list.data.length === 0 ? (
            <p className="t-sm t-secondary">No personal access tokens yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {list.data.map((row, i) => (
                <li
                  key={row.id}
                  style={{
                    padding: 'var(--sp-3) 0',
                    borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
                  }}
                >
                  <PatRow row={row} client={client} onChange={refetch} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function PatRow({
  row,
  client,
  onChange,
}: {
  row: PersonalAccessToken;
  client: CortexClient;
  onChange: () => void;
}) {
  const revoked = Boolean(row.revokedAt);
  const expired = Boolean(row.expiresAt && new Date(row.expiresAt).getTime() <= Date.now());

  const revoke = useMutation({
    mutationFn: () => client.http.delete(`v1/auth/tokens/${row.id}`),
    onSuccess: onChange,
  });

  return (
    <div style={{ ...rowH, gap: 'var(--sp-4)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...rowH, gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'var(--fw-med)', color: 'var(--text-primary)' }}>
            {row.name}
          </span>
          <span className="badge badge--neutral t-mono">mphx_{row.tokenPrefix}…</span>
          {revoked ? (
            <span className="badge badge--neutral">Revoked</span>
          ) : expired ? (
            <span className="badge badge--neutral">Expired</span>
          ) : (
            <span className="badge badge--tool">Active</span>
          )}
        </div>
        <p className="t-xs t-muted" style={{ marginTop: 2 }}>
          Created {fmtDate(row.createdAt)} by {createdByLabel(row)} · Last used{' '}
          {fmtDate(row.lastUsedAt)} · Expires {row.expiresAt ? fmtDate(row.expiresAt) : 'never'}
        </p>
        {revoke.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(revoke.error as Error).message}
          </span>
        ) : null}
      </div>
      {!revoked ? (
        <button
          type="button"
          className="btn btn--outline btn--sm"
          disabled={revoke.isPending}
          onClick={() => {
            if (
              confirm(
                `Revoke "${row.name}"? Any MCP client using this token will immediately lose access.`,
              )
            ) {
              revoke.mutate();
            }
          }}
        >
          {revoke.isPending ? 'Revoking…' : 'Revoke'}
        </button>
      ) : null}
    </div>
  );
}

function CreatePatForm({ client, onCreated }: { client: CortexClient; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [created, setCreated] = useState<CreatePersonalAccessTokenResult | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      client.http
        .post('v1/auth/tokens', {
          json: {
            name: name.trim(),
            ...(expiresInDays.trim() ? { expiresInDays: Number(expiresInDays.trim()) } : {}),
          },
        })
        .json<CreatePersonalAccessTokenResult>(),
    onSuccess: (result) => {
      setCreated(result);
      setCopied(false);
      setName('');
      setExpiresInDays('');
      onCreated();
    },
  });

  return (
    <div style={stack4}>
      {created ? (
        <div className="alert alert--success" style={stack2}>
          <p className="t-sm" style={{ fontWeight: 'var(--fw-semi)' }}>
            Token created — copy it now. You won’t be able to see it again.
          </p>
          <div style={{ ...rowH, gap: 'var(--sp-2)' }}>
            <input
              className="input t-mono"
              readOnly={true}
              value={created.token}
              style={{ flex: 1 }}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => {
                navigator.clipboard?.writeText(created.token).then(
                  () => setCopied(true),
                  () => setCopied(false),
                );
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setCreated(null)}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}

      <form
        style={rowHWrap}
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate();
        }}
      >
        <Field label="Name">
          <input
            required={true}
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ci-pipeline"
          />
        </Field>
        <Field label="Expires in (days, optional)">
          <input
            className="input"
            type="number"
            min={1}
            max={3650}
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value)}
            placeholder="never"
            style={{ maxWidth: '12rem' }}
          />
        </Field>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={create.isPending}
          style={{ alignSelf: 'flex-end' }}
        >
          {create.isPending ? 'Creating…' : 'Create token'}
        </button>
        {create.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(create.error as Error).message}
          </span>
        ) : null}
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

const codeBlock: CSSProperties = {
  margin: 0,
  padding: 'var(--sp-3)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface-2)',
  fontSize: 12,
  lineHeight: 1.5,
  overflow: 'auto',
  whiteSpace: 'pre',
};

/** How to point an MCP client / curl at the server using a `mphx_` token. */
function ConnectInstructions() {
  const mcpUrl = `${mcpServerBaseUrl}/mcp`;
  const clientConfig = `{
  "mcpServers": {
    "formant": {
      "type": "http",
      "url": "${mcpUrl}",
      "headers": { "Authorization": "Bearer mphx_…" }
    }
  }
}`;
  const curlSnippet = `curl -sN -X POST ${mcpUrl} \\
  -H 'Content-Type: application/json' \\
  -H 'Accept: application/json, text/event-stream' \\
  -H 'Authorization: Bearer mphx_…' \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}'`;

  return (
    <div className="card">
      <div className="card-header">
        <span className="t-mono-label">CONNECT</span>
        <h3 className="t-h3">How to connect to the MCP server</h3>
      </div>
      <div className="card-body" style={stack4}>
        <p className="t-sm t-secondary">
          Create a token below, then present it to the MCP server as a bearer credential. The server
          redeems it for a short-lived session scoped to your organization. Endpoint:{' '}
          <code className="t-mono">{mcpUrl}</code>
        </p>

        <div style={stack2}>
          <span className="t-mono-label">MCP CLIENT CONFIG (HTTP TRANSPORT)</span>
          <p className="t-xs t-muted">
            For any MCP client that supports an HTTP transport with custom headers (Claude Desktop,
            Inspector, etc.). Replace <code className="t-mono">mphx_…</code> with your token.
          </p>
          <pre className="t-mono" style={codeBlock}>
            {clientConfig}
          </pre>
        </div>

        <div style={stack2}>
          <span className="t-mono-label">CURL</span>
          <p className="t-xs t-muted">
            Both <code className="t-mono">initialize</code> and{' '}
            <code className="t-mono">tools/list</code> are auth-gated — send the header on every
            request.
          </p>
          <pre className="t-mono" style={codeBlock}>
            {curlSnippet}
          </pre>
        </div>

        <p className="t-xs t-muted">
          For SSE clients that can’t set headers, pass the token as a query parameter instead:{' '}
          <code className="t-mono">{mcpServerBaseUrl}/sse?token=mphx_…</code>
        </p>
      </div>
    </div>
  );
}
